import { Injectable, inject } from '@angular/core';
import { TarifdetailsBaseService } from '@comparit/calculator';
import type { ErgebnisInput, VergleichResponseMap, VorgangInput } from '@comparit/calculator';
import { TaskContextService } from '@comparit/shared';
import type { MarkedItem } from '@comparit/shared';
import type { Observable } from 'rxjs';
import { HausratRoutes } from '../app/app.routes.paths';
// Aliased: this app's service keeps the plain name, as VergleichService does.
import {
  Sparte,
  VergleichService as VergleichApi,
  WarenkorbService as WarenkorbApi,
} from '@comparit/common-api';
import { FormsService } from './forms.service';
import { ResultsService } from './results.service';

/** Hausrat's Tarifdetails: the shared page over this Sparte's Warenkorb and Vergleich endpoints. */
@Injectable({ providedIn: 'root' })
export class TarifdetailsService extends TarifdetailsBaseService {
  private readonly warenkorbApi = inject(WarenkorbApi);
  private readonly vergleichApi = inject(VergleichApi);
  private readonly results = inject(ResultsService);
  private readonly taskContext = inject(TaskContextService);
  private readonly formsService = inject(FormsService);

  readonly detailsUrl = (vorgangId: string) =>
    HausratRoutes.tarifdetails.replace(':vorgangId', vorgangId);

  readonly backUrl = HausratRoutes.rechner;

  readonly createVorgang$ = (tarif: ErgebnisInput): Observable<VorgangInput> =>
    this.warenkorbApi.createVorgang({
      sparte: Sparte.Hr,
      createVorgangRequest: {
        ergebnisIdentifier: {
          berechnungId: tarif.berechnungId,
          kombinationId: tarif.kombinationId,
        },
        taskId: this.taskContext.taskId() ?? undefined,
        versichererBafinRegNr: tarif.versichererBafinRegNr,
        versichererName: tarif.versichererName,
        tarifName: tarif.name,
      },
    });

  readonly loadVorgang$ = (vorgangId: string): Observable<VorgangInput> =>
    this.warenkorbApi.getVorgang({ sparte: Sparte.Hr, id: vorgangId });

  readonly executeGetOrder$ = (vorgangId: string): Observable<VorgangInput> =>
    this.warenkorbApi.executeVorgangGetOrder({ sparte: Sparte.Hr, id: vorgangId });

  readonly fetchVergleich$ = (identifier: MarkedItem): Observable<VergleichResponseMap> =>
    this.vergleichApi.getVergleich({ sparte: Sparte.Hr, ergebnisIdentifier: [identifier] });

  /** The current run is Hausrat's only source; kfz checks its Angebot data first. */
  readonly findTarif$ = this.results.findTarif$;

  /** The generated call already asks for a blob and types one; kfz has to pass the Accept itself. */
  protected readonly fetchDokument$ = (dokumentId: string): Observable<object> =>
    this.warenkorbApi.getVorgangDokumentInhalt({ sparte: Sparte.Hr, id: dokumentId });

  startNewJourney(): void {
    this.formsService.resetAll();
    void this.router.navigateByUrl(HausratRoutes.step(1));
  }
}
